// frontend/src/features/orders/components/Pedidos/OrderEvolutionChart.tsx
import React, { useState } from 'react';
import {
    XAxis, YAxis, CartesianGrid, Tooltip,
    ResponsiveContainer, Legend, ComposedChart, Bar, Line, LabelList
} from 'recharts';
import { EvolutionData } from '../../services/ordersAPI';

interface OrderEvolutionChartProps {
    data: EvolutionData[];
}

type MetricView = 'faturamento' | 'pares';

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        notation: 'compact',
        compactDisplay: 'short'
    } as any).format(value);

const formatNumber = (value: number) =>
    new Intl.NumberFormat('pt-BR').format(value);

const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
        return (
            <div style={{
                background: 'var(--bg-widget)',
                borderRadius: 10,
                padding: '8px 12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                border: '1px solid var(--border-primary)',
                fontSize: 12,
                minWidth: 180
            }}>
                <p style={{ fontWeight: 700, color: 'var(--text-primary, #111827)', margin: '0 0 6px', fontSize: 13 }}>
                    {label}
                </p>
                {payload.map((entry: any, index: number) => (
                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <div style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: entry.color,
                                flexShrink: 0
                            }} />
                            <span style={{ color: 'var(--text-secondary, #6b7280)', fontWeight: 500, fontSize: 13 }}>{entry.name}</span>
                        </div>
                        <span style={{ fontWeight: 700, color: 'var(--text-primary, #111827)', fontSize: 14 }}>
                            {entry.name === 'Faturamento' ? formatCurrency(entry.value) : formatNumber(entry.value)}
                        </span>
                    </div>
                ))}
            </div>
        );
    }
    return null;
};

const OrderEvolutionChart: React.FC<OrderEvolutionChartProps> = ({ data }) => {
    const [metric, setMetric] = useState<MetricView>('faturamento');

    if (!data || data.length === 0) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#999', fontSize: '14px' }}>
                Nenhum dado disponível para o período.
            </div>
        );
    }

    const barColor = '#1677ff';
    const lineColor = 'var(--theme-chart-start, #dc2626)';
    const dataKey = metric === 'faturamento' ? 'faturamento' : 'pares';
    const barName = metric === 'faturamento' ? 'Faturamento' : 'Pares';
    const lineName = 'Pedidos';

    const yAxisFormatter = metric === 'faturamento'
        ? (v: number) => formatCurrency(v)
        : (v: number) => formatNumber(v);

    return (
        <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Toggle */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '4px',
                marginBottom: '12px'
            }}>
                <div style={{
                    display: 'flex',
                    gap: '3px',
                    backgroundColor: '#f5f5f5',
                    padding: '3px',
                    borderRadius: 'var(--radius-base, 8px)'
                }}>
                    <button
                        onClick={() => setMetric('faturamento')}
                        style={{
                            border: 'none',
                            background: metric === 'faturamento' ? 'var(--bg-page-dashboard)' : 'transparent',
                            color: metric === 'faturamento' ? '#1677ff' : 'var(--text-tertiary)',
                            padding: '5px 14px',
                            borderRadius: 'var(--radius-base, 8px)',
                            cursor: 'pointer',
                            fontWeight: metric === 'faturamento' ? '600' : '400',
                            fontSize: '12px',
                            boxShadow: metric === 'faturamento' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                        }}
                    >
                        R$ Faturamento
                    </button>
                    <button
                        onClick={() => setMetric('pares')}
                        style={{
                            border: 'none',
                            background: metric === 'pares' ? 'var(--bg-page-dashboard)' : 'transparent',
                            color: metric === 'pares' ? '#1677ff' : 'var(--text-tertiary)',
                            padding: '5px 14px',
                            borderRadius: 'var(--radius-base, 8px)',
                            cursor: 'pointer',
                            fontWeight: metric === 'pares' ? '600' : '400',
                            fontSize: '12px',
                            boxShadow: metric === 'pares' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                            transition: 'all 0.2s'
                        }}
                    >
                        Pares
                    </button>
                </div>
            </div>

            {/* Chart */}
            <div style={{ width: '100%', height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={data} margin={{ top: 20, right: 10, left: 10, bottom: 0 }} barCategoryGap="20%">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis
                            dataKey="mes"
                            tick={{ fontSize: 11, fontWeight: 700, fill: '#374151' }}
                            tickLine={false}
                            axisLine={false}
                        />
                        <YAxis
                            yAxisId="left"
                            tickFormatter={yAxisFormatter}
                            tick={{ fontSize: 10, fontWeight: 700, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={false}
                            width={60}
                        />
                        <YAxis
                            yAxisId="right"
                            orientation="right"
                            tickFormatter={(v) => formatNumber(v)}
                            tick={{ fontSize: 10, fontWeight: 700, fill: '#6B7280' }}
                            tickLine={false}
                            axisLine={false}
                            width={40}
                        />
                        <Tooltip content={<CustomTooltip />} offset={20} wrapperStyle={{ zIndex: 1000, pointerEvents: 'none' }} />
                        <Legend
                            wrapperStyle={{ fontSize: 10, fontWeight: 700, paddingTop: 4 }}
                            iconType="circle"
                        />
                        <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="totalPedidos"
                            name={lineName}
                            stroke={lineColor}
                            strokeWidth={2}
                            dot={{ r: 4, fill: lineColor, strokeWidth: 2, stroke: '#fff' }}
                            activeDot={{ r: 6, strokeWidth: 2 }}
                        />
                        <Bar
                            yAxisId="left"
                            dataKey={dataKey}
                            name={barName}
                            fill={barColor}
                            radius={[0, 0, 0, 0]}
                            barSize={32}
                        >
                            <LabelList
                                dataKey={dataKey}
                                content={(props: any) => {
                                    const { value, x, y, width } = props;
                                    if (value == null) return null;
                                    const text = metric === 'faturamento' ? formatCurrency(value) : formatNumber(value);
                                    const textWidth = text.length * 5.8 + 8;
                                    const cx = x + width / 2;
                                    return (
                                        <g>
                                            <rect
                                                x={cx - textWidth / 2}
                                                y={y - 22}
                                                width={textWidth}
                                                height={16}
                                                rx={4}
                                                fill="rgba(30, 41, 59, 0.9)"
                                                stroke="rgba(71, 85, 105, 0.4)"
                                                strokeWidth={1}
                                            />
                                            <text
                                                x={cx}
                                                y={y - 10}
                                                fill="#ffffff"
                                                fontSize={9}
                                                fontWeight={700}
                                                textAnchor="middle"
                                                fontFamily="Inter, sans-serif"
                                             >
                                                {text}
                                            </text>
                                        </g>
                                    );
                                }}
                            />
                        </Bar>
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default OrderEvolutionChart;
